import { useEffect, useRef, useState } from "react";
import useLoaderStore from "../../../store/loaderStore";
import { useDelayedLoading } from "./useDelayedLoading";

// Long enough for 10 staggered rows (10 × 30ms + 300ms) to finish.
const ROWS_IN_MS = 700;

/**
 * Shared loading state for the list table shells (PaginationMainTable,
 * MainTable). Returns the delayed `showLoading` flag and the data attributes
 * the `.aone-table` CSS keys off:
 *   data-pending  request in flight, skeleton not shown yet: hide "No … found"
 *   data-loading  skeleton shown (or search cancelled): collapse real rows
 *   data-rows-in  data just replaced a skeleton: stagger the rows in
 * While loading it also hides the global top bar so the two never double up.
 */
export function useTableLoading(loading, cancelled = false) {
  const isLoading = !!loading;
  const showLoading = useDelayedLoading(isLoading);

  const enterScoped = useLoaderStore((s) => s.enterScoped);
  const leaveScoped = useLoaderStore((s) => s.leaveScoped);
  useEffect(() => {
    if (!isLoading) return undefined;
    enterScoped();
    return leaveScoped;
  }, [isLoading, enterScoped, leaveScoped]);

  const [rowsIn, setRowsIn] = useState(false);
  const wasShowing = useRef(false);
  useEffect(() => {
    if (showLoading) {
      wasShowing.current = true;
      setRowsIn(false);
      return undefined;
    }
    if (!wasShowing.current) return undefined;
    wasShowing.current = false;
    if (cancelled) return undefined;
    setRowsIn(true);
    const t = setTimeout(() => setRowsIn(false), ROWS_IN_MS);
    return () => clearTimeout(t);
  }, [showLoading, cancelled]);

  const tableProps = {
    "aria-busy": isLoading || undefined,
    "data-pending": isLoading && !showLoading ? "" : undefined,
    "data-loading": showLoading || cancelled ? "" : undefined,
    "data-rows-in": rowsIn ? "" : undefined,
  };

  return { isLoading, showLoading, tableProps };
}

export default useTableLoading;
